import { getImageForType } from "@/helpers/data.utils";
import HotpointHelper from "@/helpers/hotpointHelper";
import useWindowSize from "@/helpers/hooks/useWindowSize";
import { ArrowRight, Close } from "@mui/icons-material";
import { Button, Chip, Divider, Image } from "@nextui-org/react";
import {
  isHLSProvider,
  useMediaPlayer,
  useMediaRemote,
  useMediaState,
  useMediaStore,
} from "@vidstack/react";
import Hls from "hls.js";
import Link from "next/link";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { TbPlayerTrackNextFilled } from "react-icons/tb";
import { BiSkipNextCircle } from "react-icons/bi";
import { useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { getCMSApiRoute } from "@/helpers/api.util";
import axiosInstance from "@/config/axiosInstance";
import { getAdCountdown, getNextDueAd } from "@/utils/adScheduler";
import { useDebounceValue } from "usehooks-ts";
import { createPersistentHLSLoadPolicies, createRecoverableHLSLoader } from "@/utils/hlsLoadingPolicy";

export const mediaControlStates = {
  AirPlayButton: {
    Default: null,
    Connecting: null,
    Connected: null,
  },
  GoogleCastButton: {
    Default: null,
    Connecting: null,
    Connected: null,
  },
  PlayButton: {
    Play: null,
    Pause: null,
    Replay: null,
  },
  MuteButton: {
    Mute: null,
    VolumeLow: null,
    VolumeHigh: null,
  },
  CaptionButton: {
    On: null,
    Off: null,
  },
  PIPButton: {
    Enter: null,
    Exit: null,
  },
  FullscreenButton: {
    Enter: null,
    Exit: null,
  },
  SeekButton: {
    Backward: null,
    Forward: null,
  },
  DownloadButton: {
    Default: null,
  },
  Menu: {
    Accessibility: null,
    ArrowLeft: null,
    ArrowRight: null,
    Audio: null,
    AudioBoostUp: null,
    AudioBoostDown: null,
    Chapters: null,
    Captions: null,
    Playback: null,
    Settings: null,
    SpeedUp: null,
    SpeedDown: null,
    QualityUp: null,
    QualityDown: null,
    FontSizeUp: null,
    FontSizeDown: null,
    OpacityUp: null,
    OpacityDown: null,
    RadioCheck: null,
  },
  KeyboardDisplay: {
    Play: null,
    Pause: null,
    Mute: null,
    VolumeUp: null,
    VolumeDown: null,
    EnterFullscreen: null,
    ExitFullscreen: null,
    EnterPiP: null,
    ExitPiP: null,
    CaptionsOn: null,
    CaptionsOff: null,
    SeekForward: null,
    SeekBackward: null,
  },
};

export const PERSIAN = {
  "Caption Styles": "استایل‌های زیرنویس",
  "Captions look like this": "زیرنویس‌ها مانند این نمایش داده می‌شوند",
  "Closed-Captions Off": "زیرنویس خاموش است",
  "Closed-Captions On": "زیرنویس روشن است",
  "Display Background": "نمایش پس‌زمینه",
  "Enter Fullscreen": "ورود به تمام صفحه",
  "Enter PiP": "ورود به حالت تصویر در تصویر",
  "Exit Fullscreen": "خروج از حالت تمام صفحه",
  "Exit PiP": "خروج از حالت تصویر در تصویر",
  "Google Cast": "گوگل کست",
  "Keyboard Animations": "انیمیشن‌های صفحه کلید",
  "Seek Backward": "جستجو به عقب",
  "Seek Forward": "جستجو به جلو",
  "Skip To Live": "رفتن به زنده",
  "Text Background": "پس‌زمینه متن",
  Accessibility: "تنظیمات پیشرفته",
  AirPlay: "ایرپلی",
  Announcements: "اطلاعیه‌ها",
  Audio: "صدا",
  Auto: "اتوماتیک",
  Boost: "تقویت",
  Captions: "زیرنویس‌ها",
  Chapters: "فصل‌ها",
  Color: "رنگ",
  Connected: "متصل شد",
  Connecting: "در حال اتصال",
  Continue: "ادامه",
  Default: "پیش‌فرض",
  Disabled: "غیرفعال",
  Disconnected: "قطع ارتباط",
  Download: "دانلود",
  Family: "خانواده",
  Font: "فونت",
  Fullscreen: "تمام صفحه",
  LIVE: "زنده",
  Loop: "حلقه",
  Mute: "بی‌صدا",
  Normal: "معمولی",
  Off: "خاموش",
  Opacity: "شفافیت",
  Pause: "توقف",
  PiP: "تصویر در تصویر",
  Play: "پخش",
  Playback: "پخش",
  Quality: "کیفیت",
  Replay: "بازپخش",
  Reset: "بازنشانی",
  Seek: "جستجو",
  Settings: "تنظیمات",
  Shadow: "سایه",
  Size: "اندازه",
  Speed: "سرعت",
  Text: "متن",
  Track: "پیگیری",
  Unmute: "بازگشت صدا",
  Volume: "صدا",
};

const HLS_RECOVERY_CONFIG = {
  manifestLoadingMaxRetry: 6,
  manifestLoadingRetryDelay: 500,
  manifestLoadingMaxRetryTimeout: 8000,
  levelLoadingMaxRetry: 6,
  levelLoadingRetryDelay: 500,
  levelLoadingMaxRetryTimeout: 8000,
  fragLoadingMaxRetry: 6,
  fragLoadingRetryDelay: 500,
  fragLoadingMaxRetryTimeout: 8000,
  appendErrorMaxRetry: 4,
};

const HLS_DEFAULT_BUFFER_CONFIG = {
  backBufferLength: 30,
};

const HLS_INITIAL_LIVE_SYNC_SECONDS = 1;
const HLS_LIVE_SYNC_SEEK_TOLERANCE_SECONDS = 0.5;
const HLS_SAFE_SEEK_HANDLER_KEY = "__mnbSeekToLiveSafePosition";

const HLS_DYNAMIC_LIVE_BUFFER_CONFIG = {
  liveSyncDuration: HLS_INITIAL_LIVE_SYNC_SECONDS,
  liveMaxLatencyDuration: HLS_INITIAL_LIVE_SYNC_SECONDS * 2,
  liveSyncDurationCount: undefined,
  liveMaxLatencyDurationCount: undefined,
};

const HLS_SLOW_PROGRESS_CHECK_MS = 4000;
const HLS_SLOW_PROGRESS_MIN_RATE = 0.35;
const HLS_SLOW_PROGRESS_MAX_COUNT = 2;

const HLS_STALL_DETAILS = new Set([
  Hls.ErrorDetails.BUFFER_STALLED_ERROR,
  Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL,
]);
const HLS_RECOVERY_PROVIDERS = new WeakSet();
const HLS_LIVE_WINDOW_PROVIDERS = new WeakSet();

const safeDiagnosticUrl = (url) => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(
      url,
      typeof window !== "undefined" ? window.location.href : undefined
    );
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (_error) {
    return String(url).split(/[?#]/)[0];
  }
};

const getHLSErrorTargetUrl = (data, hls) =>
  data?.context?.url ||
  data?.networkDetails?.responseURL ||
  data?.response?.url ||
  hls?.url ||
  hls?.media?.currentSrc ||
  "";

const isHLSTimeoutError = (data) =>
  String(data?.details || "")
    .toLowerCase()
    .includes("timeout");

const buildHLSErrorMetadata = (data, hls) => {
  const targetUrl = getHLSErrorTargetUrl(data, hls);

  return {
    hlsErrorType: data?.type,
    hlsErrorDetails: data?.details,
    hlsFatal: data?.fatal,
    hlsResponseCode: data?.response?.code,
    hlsResponseText: data?.response?.text,
    hlsContextType: data?.context?.type,
    hlsContextUrl: safeDiagnosticUrl(targetUrl),
    hlsErrorMessage: data?.error?.message,
  };
};

const safePlay = (media) => {
  if (!media || !media.paused || media.ended) return;
  media.play?.().catch?.(() => {});
};

const isMediaPlaylistWithoutEndlist = (data) =>
  typeof data === "string" &&
  /(^|\n)#EXTM3U(\n|$)/.test(data) &&
  /(^|\n)#EXTINF:/m.test(data) &&
  !/(^|\n)#EXT-X-ENDLIST(\n|$)/.test(data);

const normalizeStaticHLSPlaylistResponse = (response) => {
  if (!isMediaPlaylistWithoutEndlist(response?.data)) return response;

  return {
    ...response,
    data: `${response.data.trimEnd()}\n#EXT-X-ENDLIST\n`,
  };
};

const createStaticPlaylistLoader = (BaseLoader) => {
  return class StaticPlaylistLoader extends BaseLoader {
    load(context, config, callbacks) {
      super.load(context, config, {
        ...callbacks,
        onSuccess: (response, stats, context, networkDetails) => {
          callbacks.onSuccess?.(
            normalizeStaticHLSPlaylistResponse(response),
            stats,
            context,
            networkDetails
          );
        },
      });
    }
  };
};

const getLivePlaylistFragments = (details) =>
  Array.isArray(details?.fragments) ? details.fragments.filter(Boolean) : [];

const getLivePlaylistWindowDuration = (details) => {
  if (!details?.live) return null;

  const fragments = getLivePlaylistFragments(details);
  if (!fragments.length) return null;

  const firstStart = Number(fragments[0]?.start);
  const lastEnd = Number(fragments[fragments.length - 1]?.end);
  if (Number.isFinite(firstStart) && Number.isFinite(lastEnd)) {
    const windowDuration = lastEnd - firstStart;
    if (windowDuration > 0) return windowDuration;
  }

  const durationSum = fragments.reduce((sum, fragment) => {
    const duration = Number(fragment?.duration);
    return Number.isFinite(duration) && duration > 0 ? sum + duration : sum;
  }, 0);

  return durationSum > 0 ? durationSum : null;
};

const getLivePlaylistWindowStart = (details) => {
  const fragments = getLivePlaylistFragments(details);
  const firstStart = Number(fragments[0]?.start);
  if (Number.isFinite(firstStart)) return firstStart;

  const edge = Number(details?.edge);
  const duration = getLivePlaylistWindowDuration(details);
  if (Number.isFinite(edge) && Number.isFinite(duration)) {
    return edge - duration;
  }

  return null;
};

const getLivePlaylistTargetDuration = (details) => {
  const targetDuration = Number(details?.targetduration);
  return Number.isFinite(targetDuration) && targetDuration > 0
    ? targetDuration
    : HLS_INITIAL_LIVE_SYNC_SECONDS;
};

const getHLSSafeLivePosition = (hls) => {
  const liveSyncPosition = hls?.liveSyncPosition;
  if (Number.isFinite(liveSyncPosition)) return liveSyncPosition;

  const levels = Array.isArray(hls?.levels) ? hls.levels : [];
  const currentLevel =
    Number.isInteger(hls?.currentLevel) && hls.currentLevel >= 0
      ? hls.currentLevel
      : hls?.loadLevel;
  const details =
    levels[currentLevel]?.details ||
    levels.find((level) => level?.details?.live)?.details;

  return getLivePlaylistWindowStart(details);
};

const seekHLSToLiveSafePosition = (hls) => {
  const media = hls?.media;
  const safeLivePosition = getHLSSafeLivePosition(hls);

  if (!media || !Number.isFinite(safeLivePosition)) return false;

  media.currentTime = safeLivePosition;
  return true;
};

const updateHLSLiveWindowConfig = (hls, details) => {
  const liveWindowDuration = getLivePlaylistWindowDuration(details);
  if (!Number.isFinite(liveWindowDuration) || liveWindowDuration <= 0) {
    return null;
  }

  const targetDuration = getLivePlaylistTargetDuration(details);
  const maxLatencyDuration = liveWindowDuration + targetDuration;

  hls.config.liveSyncDuration = liveWindowDuration;
  hls.config.liveMaxLatencyDuration = maxLatencyDuration;
  hls.config.maxBufferLength = Math.max(
    Number(hls.config.maxBufferLength) || 0,
    maxLatencyDuration
  );
  hls.config.maxMaxBufferLength = Math.max(
    Number(hls.config.maxMaxBufferLength) || 0,
    maxLatencyDuration
  );

  return { liveWindowDuration, targetDuration };
};

const keepPlaybackInsideLiveSafetyWindow = (hls, details, targetDuration) => {
  const media = hls?.media;
  if (!media || media.paused || media.ended || media.readyState < 1) return;

  const safeLivePosition = getHLSSafeLivePosition(hls);
  if (!Number.isFinite(safeLivePosition)) return;

  const currentTime = Number(media.currentTime);
  if (!Number.isFinite(currentTime)) return;

  const windowStart = getLivePlaylistWindowStart(details);
  const tolerance = Math.max(
    HLS_LIVE_SYNC_SEEK_TOLERANCE_SECONDS,
    Math.min(targetDuration / 2, 1)
  );
  const tooCloseToLiveEdge = currentTime > safeLivePosition + tolerance;
  const behindPlaylistWindow =
    Number.isFinite(windowStart) && currentTime < windowStart - tolerance;

  if (tooCloseToLiveEdge || behindPlaylistWindow) {
    media.currentTime = safeLivePosition;
  }
};

const installHLSLiveWindowSync = (provider) => {
  if (typeof provider.onInstance !== "function") return;
  if (HLS_LIVE_WINDOW_PROVIDERS.has(provider)) return;

  HLS_LIVE_WINDOW_PROVIDERS.add(provider);

  provider.onInstance((hls) => {
    let mediaWithSafeSeekHandler = null;

    if (hls?.userConfig) {
      hls.userConfig.liveSyncDuration =
        hls.userConfig.liveSyncDuration || HLS_INITIAL_LIVE_SYNC_SECONDS;
      hls.userConfig.liveMaxLatencyDuration =
        hls.userConfig.liveMaxLatencyDuration ||
        HLS_INITIAL_LIVE_SYNC_SECONDS * 2;
      hls.userConfig.liveSyncDurationCount = undefined;
      hls.userConfig.liveMaxLatencyDurationCount = undefined;
    }

    const detachSafeSeekHandler = () => {
      if (mediaWithSafeSeekHandler) {
        delete mediaWithSafeSeekHandler[HLS_SAFE_SEEK_HANDLER_KEY];
        mediaWithSafeSeekHandler = null;
      }
    };

    const attachSafeSeekHandler = () => {
      const media = hls?.media;
      if (!media || media === mediaWithSafeSeekHandler) return;

      detachSafeSeekHandler();
      mediaWithSafeSeekHandler = media;
      mediaWithSafeSeekHandler[HLS_SAFE_SEEK_HANDLER_KEY] = () =>
        seekHLSToLiveSafePosition(hls);
    };

    const handleLevelUpdated = (_, data) => {
      const details = data?.details;
      if (!details?.live) return;

      const liveWindow = updateHLSLiveWindowConfig(hls, details);
      if (!liveWindow) return;

      attachSafeSeekHandler();
      keepPlaybackInsideLiveSafetyWindow(
        hls,
        details,
        liveWindow.targetDuration
      );
    };

    const handleDestroying = () => {
      detachSafeSeekHandler();
      hls.off(Hls.Events.LEVEL_UPDATED, handleLevelUpdated);
      hls.off(Hls.Events.MEDIA_ATTACHED, attachSafeSeekHandler);
      hls.off(Hls.Events.MEDIA_DETACHING, detachSafeSeekHandler);
      hls.off(Hls.Events.DESTROYING, handleDestroying);
    };

    attachSafeSeekHandler();
    hls.on(Hls.Events.LEVEL_UPDATED, handleLevelUpdated);
    hls.on(Hls.Events.MEDIA_ATTACHED, attachSafeSeekHandler);
    hls.on(Hls.Events.MEDIA_DETACHING, detachSafeSeekHandler);
    hls.on(Hls.Events.DESTROYING, handleDestroying);
  });
};

const installHLSRecovery = (provider, { onPlaybackIssue } = {}) => {
  if (typeof provider.onInstance !== "function") return;
  if (HLS_RECOVERY_PROVIDERS.has(provider)) return;

  HLS_RECOVERY_PROVIDERS.add(provider);

  provider.onInstance((hls) => {
    let recoverAttempts = 0;
    let lastRecoverAt = 0;
    let retryTimer = null;
    let slowProgressTimer = null;
    let slowProgressCount = 0;
    let lastProgressSample = {
      mediaTime: 0,
      wallTime: Date.now(),
    };

    const clearRetryTimer = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const resetProgressSample = () => {
      lastProgressSample = {
        mediaTime: hls.media?.currentTime || 0,
        wallTime: Date.now(),
      };
      slowProgressCount = 0;
    };

    const recover = (reason, delay = 0) => {
      const now = Date.now();
      if (now - lastRecoverAt < 750) return;

      lastRecoverAt = now;
      clearRetryTimer();

      retryTimer = setTimeout(() => {
        retryTimer = null;

        if (!hls || hls.destroyed) return;

        try {
          const currentTime = hls.media?.currentTime;
          const isLive = Number.isFinite(hls.liveSyncPosition);
          const shouldForceLiveSeek = reason === "network";

          hls.startLoad(
            isLive && shouldForceLiveSeek
              ? -1
              : Number.isFinite(currentTime)
              ? currentTime
              : undefined
          );

          if (isLive) {
            seekHLSToLiveSafePosition(hls);
          }

          if (reason === "media" || reason === "pipeline") {
            hls.recoverMediaError();
          }

          safePlay(hls.media);
          recoverAttempts += 1;
          resetProgressSample();
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[HLSRecovery] Recovery failed:", err);
          }
        }
      }, delay);
    };

    const checkSlowProgress = () => {
      const media = hls.media;
      const isLive = Number.isFinite(hls.liveSyncPosition);

      if (
        !media ||
        !isLive ||
        (typeof document !== "undefined" && document.hidden) ||
        media.paused ||
        media.ended ||
        media.readyState < 2
      ) {
        resetProgressSample();
        return;
      }

      const now = Date.now();
      const elapsedSeconds = Math.max(
        0.001,
        (now - lastProgressSample.wallTime) / 1000
      );
      const advancedSeconds = Math.max(
        0,
        media.currentTime - lastProgressSample.mediaTime
      );
      const progressRate = advancedSeconds / elapsedSeconds;

      lastProgressSample = {
        mediaTime: media.currentTime,
        wallTime: now,
      };

      if (progressRate >= HLS_SLOW_PROGRESS_MIN_RATE) {
        slowProgressCount = 0;
        return;
      }

      slowProgressCount += 1;
      if (slowProgressCount >= HLS_SLOW_PROGRESS_MAX_COUNT) {
        slowProgressCount = 0;
        recover("pipeline");
      }
    };

    const notifyPlaybackIssue = (trigger, data) => {
      if (typeof onPlaybackIssue !== "function") return;

      onPlaybackIssue({
        trigger,
        traceTargetUrl: getHLSErrorTargetUrl(data, hls),
        metadata: buildHLSErrorMetadata(data, hls),
      });
    };

    const handleError = (_, data) => {
      if (!data) return;

      if (HLS_STALL_DETAILS.has(data.details)) {
        notifyPlaybackIssue("hlsjs_buffer_stalled", data);
        // On-demand playback must wait for the missing data. Restarting the
        // pipeline on every stall interrupts the active request/retry cycle.
        if (Number.isFinite(hls.liveSyncPosition)) recover("stall", 300);
        return;
      }

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        notifyPlaybackIssue(
          isHLSTimeoutError(data)
            ? "hlsjs_network_timeout"
            : "hlsjs_network_error",
          data
        );
        if (data.fatal) recover("network", recoverAttempts > 0 ? 500 : 0);
        return;
      }

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        if (data.fatal) recover("media");
        return;
      }
    };

    hls.on(Hls.Events.ERROR, handleError);

    slowProgressTimer = setInterval(
      checkSlowProgress,
      HLS_SLOW_PROGRESS_CHECK_MS
    );

    hls.on(Hls.Events.DESTROYING, () => {
      clearRetryTimer();
      clearInterval(slowProgressTimer);
      hls.off(Hls.Events.ERROR, handleError);

    });
  });
};

export function onProviderChange(provider, options = {}) {
  const { isLive = false } = options;

  if (isHLSProvider(provider)) {
    provider.library = Hls;
    const Loader = createRecoverableHLSLoader(provider.config?.loader || Hls.DefaultConfig.loader);
    provider.config = {
      ...provider.config,
      ...HLS_RECOVERY_CONFIG,
      ...HLS_DEFAULT_BUFFER_CONFIG,
      ...createPersistentHLSLoadPolicies(Hls.DefaultConfig, provider.config),
      loader: Loader,
      ...(provider.config?.fLoader ? { fLoader: createRecoverableHLSLoader(provider.config.fLoader) } : {}),
      ...(isLive ? HLS_DYNAMIC_LIVE_BUFFER_CONFIG : {}),
      ...(!isLive ? { pLoader: createStaticPlaylistLoader(Loader) } : {}),
    };
    installHLSLiveWindowSync(provider);
    installHLSRecovery(provider, options);
  }
}

export const AdSkipHandler = ({ setShowSkip, skipValue }) => {
  const currentTime = useMediaState("currentTime");

  useEffect(() => {
    const threshold = Number(skipValue);
    if (Number.isFinite(threshold) && threshold >= 0 && currentTime >= threshold) {
      setShowSkip(true);
    }
  }, [currentTime, setShowSkip, skipValue]);

  return null;
};

export const TitrageHandler = ({ hotPoints }) => {
  //States
  const [titrage, setTitrage] = useState(null);
  const [showHandler, setShowHandler] = useState(false);

  //Hooks
  const player = useMediaPlayer();
  const remote = useMediaRemote();
  const watchTime = useMediaState("currentTime", player);

  useEffect(() => {
    const titrageHotpoint = HotpointHelper.getIntroHotpoint(hotPoints);
    if (titrageHotpoint) {
      setTitrage(titrageHotpoint);
    }
  }, [hotPoints]);

  useEffect(() => {
    const outroHotpoint = HotpointHelper.getOutroHotpoint(hotPoints);
    const isInOutro = HotpointHelper.isPositionInHotpoint(
      parseInt(watchTime),
      outroHotpoint
    );

    if (isInOutro) {
      setShowHandler(false);
      return;
    }

    if (
      parseInt(watchTime) < titrage?.endTime &&
      parseInt(watchTime) > titrage?.startTime
    ) {
      setShowHandler(true);
    } else if (showHandler) {
      setShowHandler(false);
    }
  }, [watchTime]);

  if (showHandler) {
    return (
      <Button
        variant="light"
        className="bg-black/50 backdrop-blur-xl z-50 border-1 border-zinc-700"
        radius="full"
        disableRipple
        size="lg"
        endContent={<ArrowRight />}
        style={{ pointerEvents: "all" }}
        onClick={() => {
          remote.seek(titrage.endTime);
          remote.play();
        }}
      >
        رد کردن تیتراژ
      </Button>
    );
  }
};

export const MusicSuggestionHandler = ({
  nextMusic,
  nextMusicUrl,
  onNavigate,
  hotPoints,
}) => {
  const player = useMediaPlayer();
  const [showHandler, setShowHandler] = useState(false);
  const [countdown, setCountDown] = useState(0);
  const device = useWindowSize();
  const router = useRouter();

  const menuShown = useMediaState("controlsVisible", player);
  const watchTime = useMediaState("currentTime", player);

  const handlePlay = useCallback(() => {
    if (!nextMusic?.slug) return;
    onNavigate?.();
    router.replace(nextMusicUrl || "/player/" + nextMusic.slug);
  }, [nextMusic, nextMusicUrl, onNavigate, router]);

  useEffect(() => {
    if (watchTime > 10 && parseInt(watchTime) >= parseInt(player.duration)) {
      handlePlay();
    }

    // Use hotpoint-based timing (outro hotpoint or last 15s fallback)
    const shouldShow = HotpointHelper.shouldShowNextContent(
      watchTime,
      player.duration,
      hotPoints || [],
      "music"
    );

    if (shouldShow && parseInt(watchTime) > 0) {
      setCountDown(parseInt(player.duration - parseInt(watchTime)));
      setShowHandler(true);
    } else if (showHandler) {
      setShowHandler(false);
    }
  }, [watchTime]);

  if (!showHandler || !nextMusic) return null;

  return (
    <div
      className={`absolute z-50 ${device === "SM" && menuShown ? "top-[10rem]" : "top-[5rem]"
      } transition-all md:top-auto md:bottom-[9rem] right-[2.5%] left-5 md:left-[unset]`}
      style={{ zIndex: 999 }}
      dir="rtl"
    >
      <div
        className="rounded-2xl p-4 md:p-3 border border-white/10"
        style={{
          background:
            "linear-gradient(145deg, rgba(15,15,15,0.9) 0%, rgba(8,8,8,0.95) 100%)",
          backdropFilter: "blur(24px)",
          boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        }}
      >
        <div className="flex md:flex-col lg:items-center md:justify-center md:max-w-48 lg:max-w-96 w-full">
          <div className="relative lg:mt-3 max-w-28 md:max-w-48 lg:max-w-96 rounded-lg overflow-hidden border border-zinc-800">
            <div className="absolute inset-0 bg-zinc-800 animate-pulse" />
            <img
              src={getImageForType(
                "horizontalCover",
                nextMusic?.contentPhotos,
                device
              )}
              className="aspect-video object-cover w-full relative z-[1] transition-opacity duration-300"
              alt={nextMusic?.title}
              loading="lazy"
              onLoad={(e) => { e.target.style.opacity = 1; }}
              style={{ opacity: 0 }}
            />
          </div>
          <div className="mr-3 md:mr-0 md:mt-3 w-full">
            <h5 className="font-bold lg:text-xl text-white/90">موسیقی بعدی</h5>
            <p className="md:my-1 text-md truncate lg:text-xl text-white/70">
              {nextMusic?.title}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="lg"
                radius="full"
                variant="solid"
                className="bg-red-600 text-white hover:bg-red-700 w-full flex-auto"
                onPress={handlePlay}
              >
                پخش
              </Button>

              {countdown > 0 && (
                <Chip size="lg" variant="flat" className="mr-auto bg-white/5 text-white/80">
                  <div className="flex gap-1 items-center">
                    <BiSkipNextCircle size={device === "LG" ? 18 : 14} />
                    <span>{countdown} ثانیه</span>
                  </div>
                </Chip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NEXT_CONTENT_TIMEOUT_SECONDS = 10;

export const SeriesNextEpisodeHandler = ({ nextEpisode, rawData, hotPoints, showAfterReview, userHasReviewed }) => {
  const player = useMediaPlayer();
  const [showHandler, setShowHandler] = useState(false);
  const [countdown, setCountDown] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const autoNavigateTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const menuShown = useMediaState("controlsVisible", player);
  const watchTime = useMediaState("currentTime", player);
  const device = useWindowSize();
  const router = useRouter();

  const kind = rawData?.rawData?.contentType?.kind;
  const hasNextEpisode =
    nextEpisode?.seasonNumber != null && nextEpisode?.episodeNumber != null;
  const canShow = hasNextEpisode && (userHasReviewed || showAfterReview);
  const nextEpisodeUrl = hasNextEpisode
    ? `/player/${rawData?.rawData?.slug}?s=${nextEpisode.seasonNumber}&e=${nextEpisode.episodeNumber}`
    : "";

  const clearAutoNavigateTimers = useCallback(() => {
    if (autoNavigateTimerRef.current) {
      clearTimeout(autoNavigateTimerRef.current);
      autoNavigateTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearAutoNavigateTimers();
    setIsDismissed(false);
    setShowHandler(false);
    setCountDown(0);
  }, [nextEpisodeUrl, clearAutoNavigateTimers]);

  useEffect(() => {
    if (isDismissed || !canShow) {
      setShowHandler(false);
      return;
    }

    // Use hotpoint-based timing (outro hotpoint or last 45s fallback)
    const timing = HotpointHelper.getNextContentTriggerTime(
      player.duration,
      hotPoints || [],
      kind || "series"
    );
    const shouldShow = watchTime >= timing.triggerTime;

    if (shouldShow && canShow && parseInt(watchTime) > 0) {
      setShowHandler(true);
    } else if (showHandler && !shouldShow) {
      setShowHandler(false);
    }
  }, [watchTime, canShow, isDismissed, showHandler, player.duration, hotPoints, kind]);

  useEffect(() => {
    clearAutoNavigateTimers();

    if (!showHandler || isDismissed || !nextEpisodeUrl) return;

    setCountDown(NEXT_CONTENT_TIMEOUT_SECONDS);

    countdownTimerRef.current = setInterval(() => {
      setCountDown((prev) => Math.max(0, prev - 1));
    }, 1000);

    autoNavigateTimerRef.current = setTimeout(() => {
      router.replace(nextEpisodeUrl);
    }, NEXT_CONTENT_TIMEOUT_SECONDS * 1000);

    return clearAutoNavigateTimers;
  }, [showHandler, isDismissed, nextEpisodeUrl, router, clearAutoNavigateTimers]);

  if (!showHandler) return null;

  return (
    <div
      className={`absolute z-50 ${device === "SM" && menuShown ? "top-[10rem]" : "top-[5rem]"
      } transition-all md:top-auto md:bottom-[9rem] right-[2.5%] left-5 md:left-[unset]`}
      style={{ zIndex: 999 }}
      dir="rtl"
    >
      <div
        className="relative rounded-2xl p-4 md:p-3 border border-white/10"
        style={{
          background:
            "linear-gradient(145deg, rgba(15,15,15,0.9) 0%, rgba(8,8,8,0.95) 100%)",
          backdropFilter: "blur(24px)",
          boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        }}
      >
        <Button
          isIconOnly
          size="sm"
          radius="full"
          variant="light"
          className="absolute left-2 top-2 z-10 bg-black/50 text-white/80 hover:text-white"
          aria-label="بستن پیشنهاد قسمت بعدی"
          onPress={() => {
            setIsDismissed(true);
            setShowHandler(false);
          }}
        >
          <Close style={{ fontSize: 18 }} />
        </Button>
        <div className="flex md:flex-col lg:items-center md:justify-center md:max-w-48 lg:max-w-96 w-full">
          <div className="relative lg:mt-3 max-w-28 md:max-w-48 lg:max-w-96 rounded-lg overflow-hidden border border-zinc-800">
            <div className="absolute inset-0 bg-zinc-800 animate-pulse" />
            <img
              src={nextEpisode.cover}
              className="aspect-video object-cover w-full relative z-[1] transition-opacity duration-300"
              alt="قسمت بعدی"
              loading="lazy"
              onLoad={(e) => { e.target.style.opacity = 1; }}
              style={{ opacity: 0 }}
            />
          </div>
          <div className="mr-3 md:mr-0 md:mt-3 w-full">
            <h5 className="font-bold lg:text-xl text-white/90">قسمت بعدی</h5>
            <p className="md:my-1 text-md truncate lg:text-xl text-white/70">
              {`فصل ${nextEpisode.seasonNumber} قسمت ${nextEpisode.episodeNumber}`}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="lg"
                radius="full"
                variant="solid"
                className="bg-red-600 text-white hover:bg-red-700 w-full flex-auto"
                onPress={() => router.replace(nextEpisodeUrl)}
              >
                پخش
              </Button>

              {countdown > 0 && (
                <Chip size="lg" variant="flat" className="mr-auto bg-white/5 text-white/80">
                  <div className="flex gap-1 items-center">
                    <BiSkipNextCircle size={device === "LG" ? 18 : 14} />
                    <span>{countdown} ثانیه</span>
                  </div>
                </Chip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdHandler = ({
  disabled,
  rawData,
  adsQueue,
  playedQueueIds,
  onAdDue,
  countdownWindowSeconds = 5,
}) => {
  const player = useMediaPlayer();
  const currentTime = useMediaState("currentTime", player);
  const isLive =
    rawData?.isLive == 1 ||
    rawData?.rawData?.isLive == 1 ||
    rawData?.isLive === true ||
    rawData?.rawData?.isLive === true;
  const countdown =
    disabled || isLive
      ? null
      : getAdCountdown(
          adsQueue,
          playedQueueIds,
          currentTime,
          countdownWindowSeconds
        );

  useEffect(() => {
    if (disabled || currentTime <= 0 || isLive) {
      return;
    }

    const dueAd = getNextDueAd(adsQueue, playedQueueIds, currentTime);
    if (!dueAd) return;

    onAdDue?.(dueAd, currentTime);
  }, [adsQueue, currentTime, disabled, onAdDue, playedQueueIds, isLive]);

  if (!countdown) return null;

  const localizedSeconds = countdown.secondsRemaining.toLocaleString("fa-IR", {
    useGrouping: false,
  });

  return (
    <div className="player-ad-countdown" dir="rtl">
      <div
        className="player-ad-countdown__pill"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="player-ad-countdown__dot" aria-hidden="true" />
        <span>آگهی تا</span>
        <span className="player-ad-countdown__number">{localizedSeconds}</span>
        <span>ثانیه دیگر</span>
      </div>
    </div>
  );
};

export const WatchTracker = ({ data }) => {
  const player = useMediaPlayer();
  const watchTime = useMediaState("currentTime", player);
  const searchParams = useSearchParams();
  const [reportedTime, setReportedTime] = useDebounceValue(0, 500);

  const isLiveStream = data?.isLive == 1 || data?.rawData?.isLive == 1 || data?.isLive === true || data?.rawData?.isLive === true;

  const { slug, contentType } = data?.rawData || {};
  const s = +searchParams.get("s");
  const e = +searchParams.get("e");

  const reportWatchTime = async (time) => {
    if (isLiveStream) return;
    let _time = parseInt(time / 60);
    if (_time < 1) return;
    try {
      await axiosInstance.post(getCMSApiRoute("action/watch-tracker"), {
        contentKey: slug,
        isEpisode:
          contentType?.kind === "series" || contentType?.kind === "program"
            ? 1
            : 0,
        season: s || undefined,
        episode: e || undefined,
        time: _time,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isLiveStream) return;
    try {
      if (parseInt(watchTime) % 60 === 0 && parseInt(watchTime) >= 120) {
        setReportedTime(parseInt(watchTime));
      }
    } catch (error) {
      console.log(error);
    }
  }, [watchTime, isLiveStream]);

  useEffect(() => {
    if (isLiveStream) return;
    reportWatchTime(reportedTime);
  }, [reportedTime, isLiveStream]);

  return null;
};
